/**
 * Pure helpers for turning a saved Segment's stored query builder state into a
 * value the Velocity query builder can render, and for deriving the custom
 * field columns of a query builder value.
 *
 * Deliberately free of React, Formik, and AppSync so both transformations can
 * be exercised directly.
 */

const CUSTOM_FIELD_PREFIX = 'custom:';

/**
 * Rejection reasons returned by {@link parseQbQuery}. Stable strings, intended
 * for `console.error` context rather than for display to a user.
 *
 * - `absent`          — `null` / `undefined` input.
 * - `empty`           — a string that is empty or only whitespace.
 * - `unparsable`      — a string that is not valid JSON.
 * - `not-an-object`   — parsed to `null`, an array, or a primitive, including a
 *                       value still wrapped in a string after the two decode
 *                       attempts described in {@link parseQbQuery}.
 * - `missing-rules`   — no array `rules` property.
 * - `missing-combinator` — no string `combinator` property.
 */

/**
 * Parses a Segment's stored `query.qbQuery` into a query builder value.
 * Never throws: callers get a discriminated result so the UI can decide.
 *
 * A value that is already an object (a caller that parsed it earlier) is
 * validated and passed through unchanged, which keeps the function total.
 *
 * Tolerates one level of double encoding — see the AWSJSON note at the second
 * `JSON.parse` below.
 *
 * @param {unknown} raw The AWSJSON string stored on Segment.query.qbQuery.
 * @returns {{ ok: true, query: {rules: any[], combinator: string} } | { ok: false, reason: string }}
 */
export function parseQbQuery(raw) {
	if (raw === null || raw === undefined) {
		return { ok: false, reason: 'absent' };
	}

	let candidate = raw;

	if (typeof raw === 'string') {
		if (raw.trim().length === 0) {
			return { ok: false, reason: 'empty' };
		}

		try {
			candidate = JSON.parse(raw);
		} catch (_) {
			return { ok: false, reason: 'unparsable' };
		}

		// `qbQuery` is an AWSJSON field: AppSync validates it as JSON and hands
		// back the string a write path stored. A write path that called
		// `JSON.stringify` on a value that was ALREADY a JSON string stores a
		// double-encoded query, and one decode of that yields another string
		// rather than the query — which used to surface as `not-an-object` and a
		// segment whose rules never appeared. One extra decode recovers that
		// round trip.
		//
		// Exactly one extra attempt, never a loop: anything still a string after
		// two decodes (a triple-encoded value, say) is left alone and falls
		// through to the shape check below, so tolerating the common one-level
		// mistake does not turn the function into "decode until something
		// sticks".
		if (typeof candidate === 'string') {
			try {
				candidate = JSON.parse(candidate);
			} catch (_) {
				// The outer value decoded to a string that is not itself JSON,
				// e.g. `'"rules"'`. That is not a double-encoded query, so the
				// string is kept as the candidate and rejected below as
				// `not-an-object` — the reason it has always had. A failed second
				// decode never reports `unparsable`, which stays reserved for a
				// stored value that is not JSON at all.
			}
		}
	}

	// `JSON.parse('null')`, `'[]'`, and `'3'` all land here.
	if (
		candidate === null ||
		typeof candidate !== 'object' ||
		Array.isArray(candidate)
	) {
		return { ok: false, reason: 'not-an-object' };
	}

	if (!Array.isArray(candidate.rules)) {
		return { ok: false, reason: 'missing-rules' };
	}

	if (typeof candidate.combinator !== 'string') {
		return { ok: false, reason: 'missing-combinator' };
	}

	return { ok: true, query: candidate };
}

/**
 * Narrows a stored segment query down to the keys the query builder itself
 * needs: `combinator`, `rules`, and `id` / `not` when the stored value has
 * them. Every other key is dropped.
 *
 * ## Why this exists — do not "simplify" it away
 *
 * A Segment's `query.qbQuery` is written by whichever page saved it, and the
 * Segments page (`pages/contacts/segment.js`) keeps its Formik `query` value as
 * `{ qbQuery, searchFilter, sort }`. `QueryBuilder._notifyQueryChange` stamps
 * `query.sort = values.query.sort` onto every value it emits, so a segment
 * saved there normally carries a `sort` key *inside* its stored query builder
 * value. That is deliberate on the Segments page, which round-trips `sort`.
 *
 * On Bulk Edit it is a live hazard. Loading such a query puts `sort` on
 * `values.query`, and `QueryBuilder` has an effect keyed on
 * `[values.query.sort]` that calls `_notifyQueryChange(root)`. When a load flips
 * `values.query.sort` from `undefined` to an object in the same commit in which
 * `props.query` changes, that effect fires in the same flush as the
 * `[props.query]` effect's queued `setRoot(loadedQuery)` — but it closes over
 * the PRE-LOAD `root`. The stale empty root is pushed back out through
 * `onQueryChange` -> `applyQueryChange` -> `setFieldValue('query', ...)`, and
 * the rules that were just loaded are silently discarded. No snackbar, no
 * console error: the segment simply appears to load nothing.
 *
 * Bulk Edit compiles its own filter from `rules` / `combinator` via
 * `processFilterArray` and never reads `sort`, so carrying it is pure liability.
 * Stripping it at the load path keeps the fix off the shared `QueryBuilder`,
 * which the Segments page, the campaign dialog and the suppression tester all
 * depend on as-is.
 *
 * Pure and total: any input yields a usable builder value, the argument is never
 * mutated, and a new object is always returned. The `rules` array is passed
 * through by reference rather than cloned — it goes straight to the builder,
 * which copies before it edits.
 *
 * @param {unknown} query A parsed segment query, e.g. `parseQbQuery(...).query`.
 * @returns {{combinator: string, rules: any[], id?: string, not?: boolean}}
 */
export function toBuilderQuery(query) {
	const source = query !== null && typeof query === 'object' ? query : {};

	const builderQuery = {
		// Falling back keeps the function total for a caller that skipped
		// `parseQbQuery`. A well-formed stored query always supplies both.
		combinator: typeof source.combinator === 'string' ? source.combinator : 'and',
		rules: Array.isArray(source.rules) ? source.rules : [],
	};

	// Copied only when the stored value actually has them, so a query that never
	// carried an `id` or a `not` does not gain one here.
	if (Object.prototype.hasOwnProperty.call(source, 'id')) {
		builderQuery.id = source.id;
	}

	if (Object.prototype.hasOwnProperty.call(source, 'not')) {
		builderQuery.not = source.not;
	}

	return builderQuery;
}

/**
 * Bare names of the distinct custom fields appearing anywhere in a query value,
 * in the order first encountered. Recurses into nested rule groups.
 *
 * The `custom:` prefix is STRIPPED from every returned name. A rule whose field
 * is `custom:accountNumber` contributes `'accountNumber'`, never
 * `'custom:accountNumber'`. The prefix is only the match criterion for deciding
 * whether a rule is a custom-field rule; it is not part of the output.
 *
 * This is what the column-building useEffect in bulkEdit.js consumes: it looks
 * each name up as `userContext.customFields.find(f => f.name === name)` and
 * builds the column path `customFields.${name}`. Both the lookup and the path
 * break if the prefix is retained.
 *
 * Stateless: the result depends only on the query passed in, so re-deriving
 * from the same value always produces the same list.
 *
 * @param {{rules: any[], combinator: string}} query
 * @returns {string[]} Bare custom field names, e.g. ['accountNumber', 'dueDate'].
 */
export function deriveCustomFieldColumns(query) {
	const names = [];

	collectCustomFields(query, names);

	return names;
}

/**
 * Walks a rule group, appending each newly seen bare custom field name.
 *
 * @param {unknown} node A rule group or a rule.
 * @param {string[]} names Accumulator, mutated in place.
 * @returns {void}
 */
function collectCustomFields(node, names) {
	if (!node || typeof node !== 'object') {
		return;
	}

	if (Array.isArray(node.rules)) {
		for (const child of node.rules) {
			collectCustomFields(child, names);
		}
		return;
	}

	if (typeof node.field !== 'string' || !node.field.startsWith(CUSTOM_FIELD_PREFIX)) {
		return;
	}

	const name = node.field.slice(CUSTOM_FIELD_PREFIX.length);

	if (name.length === 0 || names.includes(name)) {
		return;
	}

	names.push(name);
}
